import type {
  LoginRequest,
  LoginResponse,
  RefreshRequest,
  RefreshResponse,
  User,
  CreateTenantRequest,
  TenantRegistrationResponse
} from '@tco/shared-types'
import envConfig from '../config/env'

interface AuthTokens {
  accessToken: string
  refreshToken: string
}

/**
 * Временный сбой обновления токена: сеть недоступна, запрос не дошёл или API
 * ответил 5xx. Сессия при этом жива — токены НЕ трогаем, иначе один потерянный
 * запрос (проснувшийся ноутбук, деплой API) разлогинивает на неделю вперёд.
 */
export class AuthNetworkError extends Error {
  readonly isTransient = true

  constructor(message: string) {
    super(message)
    this.name = 'AuthNetworkError'
  }
}

// Паузы между попытками refresh при временных сбоях.
const REFRESH_RETRY_DELAYS_MS = [500, 1500]

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

class AuthService {
  private static instance: AuthService
  private readonly API_BASE_URL = envConfig.getApiBaseUrl()
  private refreshPromise: Promise<AuthTokens> | null = null

  private constructor() {}

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  // Storage helpers
  private setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('access_token', accessToken)
    localStorage.setItem('refresh_token', refreshToken)
  }

  private getTokens(): AuthTokens | null {
    const accessToken = localStorage.getItem('access_token')
    const refreshToken = localStorage.getItem('refresh_token')

    if (!accessToken || !refreshToken) return null

    return { accessToken, refreshToken }
  }

  private clearTokens(): void {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
  }

  private setUser(user: User): void {
    localStorage.setItem('user', JSON.stringify(user))
  }

  getUser(): User | null {
    const userStr = localStorage.getItem('user')
    if (!userStr) return null

    try {
      return JSON.parse(userStr)
    } catch {
      return null
    }
  }

  // JWT token helpers
  private parseJwt(token: string): { exp: number; tenant_id?: string; tenantId?: string; sub?: string; email?: string } | null {
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch {
      return null
    }
  }

  private isTokenExpired(token: string): boolean {
    const payload = this.parseJwt(token)
    if (!payload) return true

    const now = Date.now() / 1000
    const timeUntilExpiry = payload.exp - now
    const isExpired = payload.exp < now + 30 // 30 seconds buffer

    if (isExpired) {
      console.log(`[AuthService] Token expired or expiring soon (${Math.round(timeUntilExpiry)}s until expiry)`)
    }

    return isExpired
  }

  getTenantId(): string | null {
    // First try to get from token payload
    const tokens = this.getTokens()
    if (tokens) {
      const payload = this.parseJwt(tokens.accessToken)
      if (payload?.tenantId) return payload.tenantId
      if (payload?.tenant_id) return payload.tenant_id
    }

    // Then try from user object
    const user = this.getUser()
    if (user?.tenantId) return user.tenantId
    if ((user as any)?.tenant_id) return (user as any).tenant_id

    return null
  }

  // Auth API calls
  async login(credentials: LoginRequest): Promise<User> {
    const response = await fetch(`${this.API_BASE_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Login failed' }))
      throw new Error(error.message || 'Invalid credentials')
    }

    const data: LoginResponse = await response.json()

    this.setTokens(data.accessToken, data.refreshToken)
    this.setUser(data.user)

    return data.user
  }

  async register(registrationData: CreateTenantRequest): Promise<User> {
    const response = await fetch(`${this.API_BASE_URL}/api/v1/tenants/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(registrationData),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Registration failed' }))
      throw new Error(error.message || 'Registration failed')
    }

    const data: TenantRegistrationResponse = await response.json()

    // Save tokens from registration response
    this.setTokens(data.access_token, data.refresh_token)

    // Build User object from registration response
    const user: User = {
      id: data.user.id,
      email: data.user.email,
      roleId: '', // Role ID not in response, but role_name is available
      tenantId: data.tenant_id,
      isActive: true,
      role: {
        id: '',
        name: data.user.role_name,
        permissions: { all: true }
      }
    }

    this.setUser(user)

    return user
  }

  async logout(): Promise<void> {
    const tokens = this.getTokens()

    if (tokens) {
      try {
        await fetch(`${this.API_BASE_URL}/api/v1/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken: tokens.refreshToken }),
        })
      } catch {
        // Ignore logout errors - clear local tokens anyway
      }
    }

    this.clearTokens()
  }

  private async refreshTokens(): Promise<AuthTokens> {
    const tokens = this.getTokens()
    if (!tokens) {
      console.warn('[AuthService] No refresh token available')
      throw new Error('No refresh token available')
    }

    const tenantId = this.getTenantId()
    if (!tenantId) {
      console.warn('[AuthService] No tenant ID available for refresh')
      this.clearTokens()
      throw new Error('No tenant ID available')
    }

    console.log('[AuthService] Attempting to refresh tokens...')

    let attempt = 0

    for (;;) {
      try {
        const response = await fetch(`${this.API_BASE_URL}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-Id': tenantId, // FIXED: Added X-Tenant-Id header
          },
          body: JSON.stringify({ refresh_token: tokens.refreshToken } as RefreshRequest),
        })

        // Только явный отказ сервера означает мёртвую сессию.
        if (response.status === 401 || response.status === 403) {
          const errorData = await response.json().catch(() => ({}))
          console.error('[AuthService] Refresh token rejected:', response.status, errorData)
          this.clearTokens()
          throw new Error(`Token refresh rejected: ${errorData.message || response.statusText}`)
        }

        // 5xx / 502 при деплое API — временно, токены сохраняем.
        if (!response.ok) {
          throw new AuthNetworkError(`Token refresh failed with status ${response.status}`)
        }

        const data: RefreshResponse = await response.json()
        if (!data?.accessToken || !data?.refreshToken) {
          throw new AuthNetworkError('Token refresh returned an incomplete payload')
        }

        this.setTokens(data.accessToken, data.refreshToken)

        console.log('[AuthService] Tokens refreshed successfully')

        return {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
        }
      } catch (error) {
        // fetch кидает TypeError, когда запрос вообще не ушёл (офлайн, DNS, обрыв).
        const isTransient = error instanceof AuthNetworkError || error instanceof TypeError
        if (!isTransient) {
          throw error
        }

        if (attempt >= REFRESH_RETRY_DELAYS_MS.length) {
          console.warn(
            '[AuthService] Token refresh temporarily unavailable, keeping session:',
            error
          )
          throw error instanceof AuthNetworkError
            ? error
            : new AuthNetworkError('Network error during token refresh')
        }

        await delay(REFRESH_RETRY_DELAYS_MS[attempt])
        attempt += 1
      }
    }
  }

  /**
   * Запускает refresh (или присоединяется к уже идущему).
   * Возвращает null, если сессия действительно мертва, и кидает
   * AuthNetworkError, если виновата сеть — вызывающий код должен различать.
   */
  private async runRefresh(): Promise<string | null> {
    // Prevent multiple concurrent refresh attempts
    if (!this.refreshPromise) {
      this.refreshPromise = this.refreshTokens().finally(() => {
        this.refreshPromise = null
      })
    }

    try {
      const newTokens = await this.refreshPromise
      return newTokens.accessToken
    } catch (error) {
      if (error instanceof AuthNetworkError) {
        throw error
      }
      console.error('[AuthService] Failed to get valid access token:', error)
      this.clearTokens()
      return null
    }
  }

  async getValidAccessToken(): Promise<string | null> {
    const tokens = this.getTokens()
    if (!tokens) {
      console.warn('[AuthService] No tokens available')
      return null
    }

    // If token is not expired, return it
    if (!this.isTokenExpired(tokens.accessToken)) {
      return tokens.accessToken
    }

    console.log('[AuthService] Access token expired, attempting refresh...')

    return this.runRefresh()
  }

  /**
   * Принудительное обновление — не смотрит на локальный exp. Нужно на 401 от
   * API: часы клиента могут отставать от серверных (типично после сна), и
   * «ещё валидный» по локальному времени токен сервер уже не принимает.
   *
   * staleToken — токен, с которым запрос получил 401. Если в localStorage уже
   * лежит другой, значит параллельный запрос обновил его, и повторять не нужно.
   */
  async forceRefresh(staleToken?: string): Promise<string | null> {
    const tokens = this.getTokens()
    if (!tokens) {
      console.warn('[AuthService] No tokens available for forced refresh')
      return null
    }

    if (staleToken && tokens.accessToken !== staleToken) {
      console.log('[AuthService] Token already refreshed by a parallel request')
      return tokens.accessToken
    }

    console.log('[AuthService] Forcing token refresh after 401...')

    return this.runRefresh()
  }


  // Check if user is authenticated
  isAuthenticated(): boolean {
    const tokens = this.getTokens()
    const user = this.getUser()
    return !!(tokens && user)
  }

  // Auto-refresh setup for background token refresh
  startAutoRefresh(): () => void {
    console.log('[AuthService] Starting auto-refresh mechanism (checking every minute + visibility change)')

    // Interval-based check (works when tab is active)
    const interval = setInterval(async () => {
      const tokens = this.getTokens()
      if (!tokens) return

      // Refresh if token expires in next 5 minutes
      const payload = this.parseJwt(tokens.accessToken)
      if (payload) {
        const now = Date.now() / 1000
        const timeUntilExpiry = payload.exp - now

        // Log token status every 5 minutes if not about to expire
        if (Math.round(timeUntilExpiry) % 300 === 0) {
          console.log(`[AuthService] Token health check: ${Math.round(timeUntilExpiry / 60)} minutes until expiry`)
        }

        if (timeUntilExpiry < 300) { // 5 minutes
          console.warn(`[AuthService] Token expiring soon (${Math.round(timeUntilExpiry)}s), triggering proactive refresh...`)
          try {
            await this.getValidAccessToken()
            console.log('[AuthService] Proactive token refresh successful')
          } catch (error) {
            console.error('[AuthService] Proactive token refresh failed:', error)
            // Token refresh failed - user will be logged out on next API call
          }
        }
      }
    }, 60000) // Check every minute

    // Visibility change handler - CRITICAL for inactive tabs
    // Browser throttles setInterval for inactive tabs, so we need this
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[AuthService] Tab became visible, checking token validity...')

        const tokens = this.getTokens()
        if (!tokens) {
          console.warn('[AuthService] No tokens found on tab focus')
          return
        }

        const payload = this.parseJwt(tokens.accessToken)
        if (payload) {
          const now = Date.now() / 1000
          const timeUntilExpiry = payload.exp - now

          console.log(`[AuthService] Token status on tab focus: ${Math.round(timeUntilExpiry)}s until expiry`)

          // If token is expired or expires in next 2 minutes, refresh immediately
          if (timeUntilExpiry < 120) {
            console.warn(`[AuthService] Token expired or expiring soon on tab focus, refreshing immediately...`)
            try {
              await this.getValidAccessToken()
              console.log('[AuthService] Token refreshed successfully on tab focus')
            } catch (error) {
              console.error('[AuthService] Token refresh on tab focus failed:', error)
              // Don't force logout here - let the next API call handle it
              // This gives user a chance to see the login page naturally
            }
          }
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Also check on window focus (some browsers don't fire visibilitychange correctly)
    const handleWindowFocus = async () => {
      // Small delay to avoid duplicate calls with visibilitychange
      setTimeout(async () => {
        if (document.visibilityState === 'visible') {
          const tokens = this.getTokens()
          if (!tokens) return

          const payload = this.parseJwt(tokens.accessToken)
          if (payload) {
            const now = Date.now() / 1000
            const timeUntilExpiry = payload.exp - now

            // Only refresh if very close to expiry (avoid duplicate refresh with visibilitychange)
            if (timeUntilExpiry < 60) {
              console.warn(`[AuthService] Token critical on window focus (${Math.round(timeUntilExpiry)}s), refreshing...`)
              try {
                await this.getValidAccessToken()
              } catch (error) {
                console.error('[AuthService] Token refresh on window focus failed:', error)
              }
            }
          }
        }
      }, 500)
    }

    window.addEventListener('focus', handleWindowFocus)

    // Сеть вернулась (проснулся ноутбук, переключился Wi-Fi) — пробуем обновить
    // сразу, не дожидаясь минутного тика или следующего запроса пользователя.
    const handleOnline = async () => {
      const tokens = this.getTokens()
      if (!tokens || !this.isTokenExpired(tokens.accessToken)) return

      console.log('[AuthService] Network is back, refreshing expired token...')
      try {
        await this.getValidAccessToken()
      } catch (error) {
        console.warn('[AuthService] Token refresh on network recovery failed:', error)
      }
    }

    window.addEventListener('online', handleOnline)

    return () => {
      console.log('[AuthService] Stopping auto-refresh mechanism')
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleWindowFocus)
      window.removeEventListener('online', handleOnline)
    }
  }
}

export default AuthService